import React, {
  useState,
  useEffect,
  useRef,
  createContext,
  useContext,
} from "react"
import styled from "styled-components"

import Box from "../atoms/Box"
import Flex from "../atoms/Flex"
import SliderControlArrow from "../icons/SliderControlArrow"
import Indicator from "../atoms/Indicator"
import { compose, space, color } from "styled-system"

const StyledButton = styled("button")`
  ${compose(space, color)}
  svg {
    ${props => props.$flip && "transform: scaleX(-1);"}
  }
`

const CarouselContext = createContext(null)

const useCarousel = () => {
  const ctx = useContext(CarouselContext)
  if (!ctx) {
    throw new Error(
      "GenericCarouselWrapper.Track and .Controls must be inside <GenericCarouselWrapper>",
    )
  }
  return ctx
}

const GenericCarouselWrapper = ({
  children, // children will be <GenericCarouselWrapper.Track> and <GenericCarouselWrapper.Controls>
  spacing,
  size,
}) => {
  const CARD_MARGIN = spacing
  const CARD_WIDTH = size || 300

  const [internalChildren, setInternalChildren] = useState([])
  const [originalCount, setOriginalCount] = useState(0)
  const [extendedChildren, setExtendedChildren] = useState([])
  const [middleStartIndex, setMiddleStartIndex] = useState(0)

  const [activeIndex, setActiveIndex] = useState(0)
  const [isTransitioning, setIsTransitioning] = useState(true)
  const containerRef = useRef(null)

  // expose this so Track can calculate translateX
  const getTranslateX = () => {
    const containerWidth = containerRef.current?.offsetWidth || 0
    const cardWidthWithMargin = CARD_WIDTH + CARD_MARGIN
    const offset = activeIndex * cardWidthWithMargin
    const centerOffset = (containerWidth - CARD_WIDTH) / 2
    return offset - centerOffset
  }

  // initialize items
  useEffect(() => {
    if (!children) return
    // Track will populate items via context setter
  }, [children])

  // infinite wrap-around logic
  useEffect(() => {
    if (!originalCount) return
    const buffer = originalCount
    const totalLength = extendedChildren.length

    if (activeIndex <= buffer || activeIndex >= totalLength - buffer) {
      setTimeout(() => setIsTransitioning(false), 350)
      const normalized = middleStartIndex + (activeIndex % originalCount)
      setTimeout(() => {
        setActiveIndex(normalized)
        setTimeout(() => setIsTransitioning(true), 30)
      }, 400)
    }
  }, [activeIndex, originalCount, extendedChildren.length, middleStartIndex])

  // methods for buttons
  const handlePrev = () => setActiveIndex(prev => prev - 1)
  const handleNext = () => setActiveIndex(prev => prev + 1)

  const focusedLogicalIndex = originalCount ? activeIndex % originalCount : 0

  return (
    <CarouselContext.Provider
      value={{
        // exposed to Track component
        CARD_WIDTH,
        CARD_MARGIN,
        containerRef,
        internalChildren,
        extendedChildren,
        getTranslateX,
        isTransitioning,
        setInternalChildren,
        setOriginalCount,
        setExtendedChildren,
        setMiddleStartIndex,
        middleStartIndex,

        // exposed to Controls
        handlePrev,
        handleNext,
        setActiveIndex,
        focusedLogicalIndex,
      }}
    >
      {children}
    </CarouselContext.Provider>
  )
}

export default GenericCarouselWrapper

GenericCarouselWrapper.Track = function Track({ children }) {
  const {
    CARD_WIDTH,
    CARD_MARGIN,
    internalChildren,
    extendedChildren,
    setInternalChildren,
    setOriginalCount,
    setExtendedChildren,
    setMiddleStartIndex,
    containerRef,
    getTranslateX,
    isTransitioning,
  } = useCarousel()

  // store children from user
  useEffect(() => {
    const arr = React.Children.toArray(children)
    setInternalChildren(arr)
    setOriginalCount(arr.length)
    setExtendedChildren(Array(5).fill(arr).flat())
    setMiddleStartIndex(arr.length * 2)
  }, [children])

  return (
    <Flex
      flexDirection="column"
      overflow="hidden"
      width="100%"
      ref={containerRef}
    >
      <Box position="relative">
        <Flex
          flexDirection="row"
          justifyContent="flex-start"
          alignItems="center"
          style={{
            transition: isTransitioning ? "transform 0.4s ease" : "none",
            transform: `translateX(-${getTranslateX()}px)`,
            gap: `${CARD_MARGIN}px`,
          }}
        >
          {extendedChildren.map((child, i) => (
            <Box
              key={i}
              width={`${CARD_WIDTH}px`}
              flexShrink={0}
              display="flex"
              alignItems="stretch"
              my={6}
            >
              {child}
            </Box>
          ))}
        </Flex>
      </Box>
    </Flex>
  )
}

GenericCarouselWrapper.Controls = function Controls({
  arrows,
  indicators,
  indicatorsProps,
  count,
}) {
  const {
    handlePrev,
    handleNext,
    setActiveIndex,
    middleStartIndex,
    focusedLogicalIndex,
  } = useCarousel()

  return (
    <Flex
      justifyContent={
        indicatorsProps?.controlsAlignment === "center"
          ? "center"
          : indicatorsProps?.controlsAlignment === "left"
            ? "flex-start"
            : "flex-end"
      }
      alignItems="center"
      mt={6}
    >
      {arrows && (
        <StyledButton $flip={true} mr={4} onClick={handlePrev}>
          <SliderControlArrow direction="right" />
        </StyledButton>
      )}

      {indicators &&
        [...Array(count)].map((_, idx) => (
          <Flex alignItems="center" key={idx} px={3}>
            <Indicator
              onClick={() => setActiveIndex(middleStartIndex + idx)}
              active={focusedLogicalIndex === idx}
            />
          </Flex>
        ))}

      {arrows && (
        <StyledButton ml={4} onClick={handleNext}>
          <SliderControlArrow direction="right" />
        </StyledButton>
      )}
    </Flex>
  )
}
